import { cn, Stack, StackProps } from '@landler/component-library';
import React from 'react';

export type ProgressProps = {
  /**
   * Current step (completed steps)
   */
  currentStep: number;
  /**
   * Total number of steps
   */
  totalSteps: number;
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Height of the progress bar in pixels
   * @default 4
   */
  height?: number;
  /**
   * Color of the filled progress bar
   * @default 'bg-success-main'
   */
  fillColor?: string;
  /**
   * Color of the unfilled progress bar
   * @default 'bg-neutral-200'
   */
  trackColor?: string;
  /**
   * Whether to show the step indicator text (e.g., "1 / 6")
   * @default true
   */
  showStepIndicator?: boolean;
  /**
   * Custom className for the step indicator text
   */
  stepIndicatorClassName?: string;
  /**
   * Direction of the progress bar layout
   * @default 'row'
   */
  direction?: 'row' | 'column';
} & StackProps;

export const ProgressWidget: React.FC<ProgressProps> = ({
  currentStep,
  totalSteps,
  className,
  height = 4,
  fillColor = 'bg-primary-100',
  trackColor = 'bg-primary-12',
  showStepIndicator = true,
  stepIndicatorClassName,
  direction = 'row',
  ...delegated
}) => {
  const normalizedCurrentStep = Math.max(0, Math.min(currentStep, totalSteps));
  const progressPercentage = (normalizedCurrentStep / totalSteps) * 100;

  return (
    <Stack className={cn('w-full items-center justify-between', className)} direction={direction} {...delegated}>
      <div className='relative flex-grow mr-4'>
        <div className={cn('w-full rounded-full', trackColor)} style={{ height: `${height}px` }} />
        <div
          className={cn('absolute top-0 left-0 rounded-full transition-width duration-300 ease-in-out', fillColor)}
          style={{
            height: `${height}px`,
            width: `${progressPercentage}%`,
          }}
        />
      </div>
      {showStepIndicator && (
        <div className={cn('whitespace-nowrap typography-body2 text-text-secondary', stepIndicatorClassName)}>
          {normalizedCurrentStep} / {totalSteps}
        </div>
      )}
    </Stack>
  );
};
